import React, { useEffect, createElement } from 'react';
interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
  faq?: Array<{
    question: string;
    answer: string;
  }>;
}
function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function setJsonLd(data: Record<string, unknown>) {
  const id = 'seo-json-ld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
function removeJsonLd() {
  const el = document.getElementById('seo-json-ld');
  if (el) el.remove();
}
const BASE_URL = 'https://viridiaanalytics.com';
export function SEOHead({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage = `${BASE_URL}/og-image.png`,
  noindex = false,
  jsonLd,
  faq
}: SEOHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;
    // Core meta
    setMetaTag('description', description);
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    // Canonical, always strip query parameters
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : `${BASE_URL}${window.location.pathname}`;
    setCanonical(canonicalUrl);
    // Open Graph
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:site_name', 'Viridia Analytics', true);
    setMetaTag('og:locale', 'en_US', true);
    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    // Geo tags for local SEO
    setMetaTag('geo.region', 'US-AZ');
    setMetaTag('geo.placename', 'Phoenix');
    // JSON-LD
    let finalJsonLd: any = null;
    if (jsonLd && faq) {
      // If both exist, we can output an array of schemas
      finalJsonLd = [jsonLd, {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      }];
    } else if (jsonLd) {
      finalJsonLd = jsonLd;
    } else if (faq) {
      finalJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      };
    }
    if (finalJsonLd) {
      setJsonLd(finalJsonLd);
    } else {
      removeJsonLd();
    }
    return () => {
      // Cleanup JSON-LD on unmount
      removeJsonLd();
    };
  }, [title, description, canonical, keywords, ogType, ogImage, noindex, jsonLd, faq]);
  return null;
}

/* 2026-09-08 (compliance, Kevin): Yelp flagged Viridia for review
   solicitation. This schema is injected on the homepage, so it is the most
   machine-readable claim on the whole site. Two things were removed:

     1. a full schema.org Service offer named "Reputation Management",
        described as "Automated review requests and management across
        platforms to build trust and local authority";
     2. "reputation management" from knowsAbout.

   Do not reinstate either. No Viridia asset claims to request, generate,
   solicit, automate or manage reviews.

   2026-09-17 (local search build): areaServed now covers the full Phoenix
   metro (Gilbert, Tempe, Surprise, Goodyear added), and the offer catalog
   carries the four local-search service pages so the organization node
   links to them. Nothing about reviews was added. */

// Pre-built JSON-LD for the business
export const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'ProfessionalService',
    '@id': `${BASE_URL}/#organization`,
    name: 'Viridia Analytics',
    description: 'Phoenix agency for home service businesses: The Viridia System (CRM, AI receptionist, automation), Yelp Ads management, and website design and hosting.',
    url: BASE_URL,
    telephone: '+16233000708',
    email: 'info@viridiaanalytics.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Phoenix',
      addressRegion: 'AZ',
      postalCode: '85050',
      addressCountry: 'US'
    },
    foundingDate: '2025',
    founder: {
      '@id': `${BASE_URL}/#founder`
    },
    areaServed: [{
      '@type': 'City',
      name: 'Phoenix'
    }, {
      '@type': 'City',
      name: 'Scottsdale'
    }, {
      '@type': 'City',
      name: 'Glendale'
    }, {
      '@type': 'City',
      name: 'Peoria'
    }, {
      '@type': 'City',
      name: 'Chandler'
    }, {
      '@type': 'City',
      name: 'Mesa'
    }, {
      '@type': 'City',
      name: 'Gilbert'
    }, {
      '@type': 'City',
      name: 'Tempe'
    }, {
      '@type': 'City',
      name: 'Queen Creek'
    }, {
      '@type': 'City',
      name: 'Buckeye'
    }, {
      '@type': 'City',
      name: 'Surprise'
    }, {
      '@type': 'City',
      name: 'Goodyear'
    }, {
      '@type': 'City',
      name: 'Long Beach'
    }, {
      '@type': 'City',
      name: 'Los Angeles'
    }],
    priceRange: '$$',
    sameAs: ['https://www.facebook.com/profile.php?id=61571074363261', 'https://www.instagram.com/viridiaanalyticsllc/', 'https://www.linkedin.com/company/viridia-analytics/', 'https://www.yelp.com/biz/viridia-analytics-phoenix-2'],
    knowsAbout: ['AI lead conversion', 'AI marketing agency', 'AI advertising', 'answering service for contractors', 'AI receptionist', 'missed call text back', 'Yelp advertising', 'Google Ads management', 'Google Business Profile setup', 'local SEO', 'home services marketing', 'contractor marketing', 'HVAC marketing', 'painter marketing', 'landscaper marketing', 'window installer marketing', 'marketing automation', 'CRM systems'],
    slogan: 'Stop Losing Leads. Start Growing.',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Services',
      itemListElement: [{
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'The Viridia System',
          description: 'One CRM, AI receptionist, and automation package for home service businesses: every call, text, and Yelp lead answered, follow-up, scheduling, and invoicing in one place.',
          url: `${BASE_URL}/how-it-works`
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Answering Service for Contractors',
          description: 'AI receptionist for contractors that answers every call around the clock, books estimates on the calendar and texts back missed calls.',
          url: `${BASE_URL}/answering-service-for-contractors`
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Missed-Call Text-Back',
          description: 'Automatic text back to every missed call within seconds, from a registered business number, with replies in one inbox.',
          url: `${BASE_URL}/missed-call-text-back`
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'CRM for Contractors',
          description: 'One inbox for calls, texts, Yelp and Google, a job pipeline, follow-ups that run on their own, and invoicing with QuickBooks sync.',
          url: `${BASE_URL}/crm-for-contractors`
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Google Business Profile Setup',
          description: 'One-time Google Business Profile setup and verification for Phoenix home service businesses.',
          url: `${BASE_URL}/google-business-profile-setup`
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Yelp Ads Management',
          description: 'Yelp advertising run by a former Yelp rep: ad targeting, page upgrade optimization, on-page SEO, content, and a monthly report tied to booked jobs.',
          url: `${BASE_URL}/yelp-ads`
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Website Design and Hosting',
          description: 'Fast, conversion-focused websites for local service businesses, built and hosted by Viridia.',
          url: `${BASE_URL}/websites`
        }
      }]
    }
  }, {
    '@type': 'Person',
    '@id': `${BASE_URL}/#founder`,
    name: 'Kevin Rogers',
    jobTitle: 'Founder & CEO',
    worksFor: {
      '@id': `${BASE_URL}/#organization`
    },
    description: 'Founder of Viridia Analytics. Former shower installer and cabinet finisher turned ad platform insider, specializing in AI-powered lead conversion and digital advertising for local service businesses and contractors.',
    sameAs: ['https://www.linkedin.com/in/kevinrogers-viridia/']
  }]
};